import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { slugify } from '../../utils/slug';
import { Group } from '../../types/groups';
import { CATEGORY_EMOJI, CATEGORY_GRADIENT } from '../../constants/groups';
import { UserCoords, calcDistance, formatDistance } from '../../hooks/useUserLocation';

interface GroupCardProps {
  group: Group;
  userCoords?: UserCoords | null;
}

export default function GroupCard({ group, userCoords }: GroupCardProps) {
  const distance = userCoords && group.latitude && group.longitude
    ? formatDistance(calcDistance(userCoords.lat, userCoords.lng, group.latitude, group.longitude))
    : null;
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);

  const categoryEmoji = CATEGORY_EMOJI[group.category] || '💬';
  const gradient = CATEGORY_GRADIENT[group.category] || CATEGORY_GRADIENT['Other'];
  const images = group.images?.filter(Boolean) || [];
  const hasImage = images.length > 0;
  const multiImage = images.length > 1;

  return (
    <div
      onClick={() => navigate(`/groups/${slugify(group.name)}-${group.id}?community=${encodeURIComponent(group.community || '')}`)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: '#ffffff',
        border: hovered ? '1.5px solid #FF385C' : '1.5px solid #eef0f6',
        borderRadius: '16px',
        overflow: 'hidden',
        cursor: 'pointer',
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
        boxShadow: hovered
          ? '0 12px 40px rgba(255,56,92,0.12)'
          : '0 2px 12px rgba(0,0,0,0.05)',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Image / Placeholder */}
      <div
        style={{
          width: '100%',
          aspectRatio: '16/9',
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {hasImage ? (
          <>
            <img
              src={images[imgIndex]}
              alt={group.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                transition: 'transform 0.3s ease',
                transform: hovered ? 'scale(1.04)' : 'scale(1)',
              }}
            />
            {multiImage && (
              <>
                {/* Prev */}
                <button
                  onClick={(e) => { e.stopPropagation(); setImgIndex(i => (i - 1 + images.length) % images.length); }}
                  style={{
                    position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%',
                    width: '24px', height: '24px', cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  }}
                >‹</button>
                {/* Next */}
                <button
                  onClick={(e) => { e.stopPropagation(); setImgIndex(i => (i + 1) % images.length); }}
                  style={{
                    position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%',
                    width: '24px', height: '24px', cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  }}
                >›</button>
                {/* Dots */}
                <div style={{ position: 'absolute', bottom: '6px', left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: '4px' }}>
                  {images.map((_, i) => (
                    <div
                      key={i}
                      onClick={(e) => { e.stopPropagation(); setImgIndex(i); }}
                      style={{
                        width: i === imgIndex ? '14px' : '6px', height: '6px',
                        borderRadius: '3px', cursor: 'pointer',
                        backgroundColor: i === imgIndex ? '#fff' : 'rgba(255,255,255,0.55)',
                        transition: 'all 0.2s',
                      }}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              background: gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: '3rem', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' }}>
              {categoryEmoji}
            </span>
          </div>
        )}

        {/* Badges overlay */}
        <div
          style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            display: 'flex',
            gap: '4px',
          }}
        >
          {group.status === 'suspended' && (
            <span
              style={{
                backgroundColor: '#ef4444',
                color: '#fff',
                fontSize: '0.65rem',
                fontWeight: 700,
                padding: '3px 8px',
                borderRadius: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
              }}
            >
              Suspended
            </span>
          )}
          {group.is_private && (
            <span
              style={{
                backgroundColor: 'rgba(255,255,255,0.92)',
                color: '#92400e',
                fontSize: '0.65rem',
                fontWeight: 600,
                padding: '3px 8px',
                borderRadius: '10px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                backdropFilter: 'blur(4px)',
              }}
            >
              🔒 Private
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '1rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <h3
          style={{
            color: '#1a1f36',
            fontWeight: 700,
            fontSize: '0.95rem',
            margin: '0 0 0.375rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {group.name}
        </h3>

        {(group.location || distance) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '0 0 0.25rem', minWidth: 0 }}>
            <span style={{ color: '#6b7a99', fontSize: '0.78rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>📍 {group.location}</span>
            {distance && <span style={{ color: '#10b981', fontSize: '0.78rem', fontWeight: 600, flexShrink: 0 }}>{distance}</span>}
          </div>
        )}

        <p
          style={{
            color: '#6b7a99',
            fontSize: '0.82rem',
            margin: '0 0 0.875rem',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: '1.5',
            flex: 1,
          }}
        >
          {group.description}
        </p>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: '0.75rem',
            borderTop: '1px solid #f0f2f7',
            marginTop: 'auto',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span
              style={{
                color: '#10B981',
                fontSize: '0.8rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
              }}
            >
              👥 {group.member_count.toLocaleString()}
              {group.community && (
                <span style={{ color: '#a0aec0', fontWeight: 400 }}>· {group.community}</span>
              )}
            </span>
          </div>

          <span
            style={{
              backgroundColor: '#f5f7ff',
              color: '#4a5568',
              fontSize: '0.72rem',
              padding: '3px 10px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              fontWeight: 500,
              border: '1px solid #eef0f6',
            }}
          >
            {categoryEmoji} {group.category}
          </span>
        </div>
      </div>
    </div>
  );
}
